import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { geoOrthographic, geoPath, geoGraticule } from 'd3-geo';
import { select } from 'd3-selection';
import { drag } from 'd3-drag';
import { json } from 'd3-fetch';
import * as topojson from 'topojson-client';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiGlobe, FiNavigation } = FiIcons;

const MapDisplay = ({ originalLocation, antipodeLocation }) => {
  const svgRef = useRef(null);
  const projectionRef = useRef(null);
  const pathRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current) return;

    const width = 800;
    const height = 600;
    const sensitivity = 75;

    // Create projection
    const projection = geoOrthographic()
      .scale(250)
      .center([0, 0])
      .rotate([0, -30])
      .translate([width / 2, height / 2]);

    projectionRef.current = projection;

    // Create path generator
    const path = geoPath().projection(projection);
    pathRef.current = path;

    // Create SVG
    const svg = select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .style('background', 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)');

    // Clear previous content
    svg.selectAll('*').remove();

    // Create main group
    const g = svg.append('g');

    // Add graticule (grid lines)
    const graticule = geoGraticule();
    g.append('path')
      .datum(graticule())
      .attr('class', 'graticule')
      .attr('d', path)
      .attr('fill', 'none')
      .attr('stroke', 'rgba(255, 255, 255, 0.1)')
      .attr('stroke-width', 0.5);

    // Load world map data
    json('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json').then(world => {
      // Draw countries
      g.append('g')
        .selectAll('path')
        .data(topojson.feature(world, world.objects.countries).features)
        .enter().append('path')
        .attr('d', path)
        .attr('fill', '#1e293b')
        .attr('stroke', '#334155')
        .attr('stroke-width', 0.5)
        .attr('opacity', 0.8);

      // Add ocean fill
      g.append('circle')
        .attr('cx', width / 2)
        .attr('cy', height / 2)
        .attr('r', projection.scale())
        .attr('fill', 'rgba(59, 130, 246, 0.1)')
        .attr('stroke', 'rgba(59, 130, 246, 0.3)')
        .attr('stroke-width', 2);

      // Add location markers
      if (originalLocation && antipodeLocation) {
        // Original location marker
        const originalCoords = projection([originalLocation.lng, originalLocation.lat]);
        if (originalCoords) {
          g.append('circle')
            .attr('cx', originalCoords[0])
            .attr('cy', originalCoords[1])
            .attr('r', 8)
            .attr('fill', '#3b82f6')
            .attr('stroke', '#fff')
            .attr('stroke-width', 2)
            .attr('class', 'location-marker original');

          // Add pulsing effect
          g.append('circle')
            .attr('cx', originalCoords[0])
            .attr('cy', originalCoords[1])
            .attr('r', 8)
            .attr('fill', 'none')
            .attr('stroke', '#3b82f6')
            .attr('stroke-width', 2)
            .attr('opacity', 0.6)
            .attr('class', 'pulse-original');
        }

        // Antipode location marker
        const antipodeCoords = projection([antipodeLocation.lng, antipodeLocation.lat]);
        if (antipodeCoords) {
          g.append('circle')
            .attr('cx', antipodeCoords[0])
            .attr('cy', antipodeCoords[1])
            .attr('r', 8)
            .attr('fill', '#22c55e')
            .attr('stroke', '#fff')
            .attr('stroke-width', 2)
            .attr('class', 'location-marker antipode');

          // Add pulsing effect
          g.append('circle')
            .attr('cx', antipodeCoords[0])
            .attr('cy', antipodeCoords[1])
            .attr('r', 8)
            .attr('fill', 'none')
            .attr('stroke', '#22c55e')
            .attr('stroke-width', 2)
            .attr('opacity', 0.6)
            .attr('class', 'pulse-antipode');
        }

        // Add connection arc
        const arc = geoPath()
          .projection(projection)
          .pointRadius(2);

        const connectionData = {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [originalLocation.lng, originalLocation.lat],
              [antipodeLocation.lng, antipodeLocation.lat]
            ]
          }
        };

        g.append('path')
          .datum(connectionData)
          .attr('d', arc)
          .attr('fill', 'none')
          .attr('stroke', '#8b5cf6')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '5,5')
          .attr('opacity', 0.8)
          .attr('class', 'connection-line');
      }
    });

    // Add drag functionality
    const dragHandler = drag()
      .on('start', () => {
        svg.select('.graticule').attr('opacity', 0.2);
      })
      .on('drag', (event) => {
        const rotate = projection.rotate();
        const k = sensitivity / projection.scale();
        projection.rotate([rotate[0] + event.dx * k, rotate[1] - event.dy * k]);
        
        // Redraw everything
        svg.selectAll('path').attr('d', path);
        svg.selectAll('.location-marker, .pulse-original, .pulse-antipode, .connection-line').remove();

        // Redraw markers
        if (originalLocation && antipodeLocation) {
          const originalCoords = projection([originalLocation.lng, originalLocation.lat]);
          if (originalCoords && originalCoords[0] >= 0 && originalCoords[0] <= width && 
              originalCoords[1] >= 0 && originalCoords[1] <= height) {
            g.append('circle')
              .attr('cx', originalCoords[0])
              .attr('cy', originalCoords[1])
              .attr('r', 8)
              .attr('fill', '#3b82f6')
              .attr('stroke', '#fff')
              .attr('stroke-width', 2)
              .attr('class', 'location-marker original');
          }

          const antipodeCoords = projection([antipodeLocation.lng, antipodeLocation.lat]);
          if (antipodeCoords && antipodeCoords[0] >= 0 && antipodeCoords[0] <= width && 
              antipodeCoords[1] >= 0 && antipodeCoords[1] <= height) {
            g.append('circle')
              .attr('cx', antipodeCoords[0])
              .attr('cy', antipodeCoords[1])
              .attr('r', 8)
              .attr('fill', '#22c55e')
              .attr('stroke', '#fff')
              .attr('stroke-width', 2)
              .attr('class', 'location-marker antipode');
          }
        }
      })
      .on('end', () => {
        svg.select('.graticule').attr('opacity', 1);
      });

    svg.call(dragHandler);

    // Add auto-rotation
    let rotation = 0;
    const rotate = () => {
      rotation += 0.2;
      projection.rotate([rotation, -30]);
      
      svg.selectAll('path').attr('d', path);
      svg.selectAll('.location-marker, .pulse-original, .pulse-antipode, .connection-line').remove();

      // Redraw markers
      if (originalLocation && antipodeLocation) {
        const originalCoords = projection([originalLocation.lng, originalLocation.lat]);
        if (originalCoords && originalCoords[0] >= 0 && originalCoords[0] <= width && 
            originalCoords[1] >= 0 && originalCoords[1] <= height) {
          g.append('circle')
            .attr('cx', originalCoords[0])
            .attr('cy', originalCoords[1])
            .attr('r', 8)
            .attr('fill', '#3b82f6')
            .attr('stroke', '#fff')
            .attr('stroke-width', 2)
            .attr('class', 'location-marker original');
        }

        const antipodeCoords = projection([antipodeLocation.lng, antipodeLocation.lat]);
        if (antipodeCoords && antipodeCoords[0] >= 0 && antipodeCoords[0] <= width && 
            antipodeCoords[1] >= 0 && antipodeCoords[1] <= height) {
          g.append('circle')
            .attr('cx', antipodeCoords[0])
            .attr('cy', antipodeCoords[1])
            .attr('r', 8)
            .attr('fill', '#22c55e')
            .attr('stroke', '#fff')
            .attr('stroke-width', 2)
            .attr('class', 'location-marker antipode');
        }
      }
    };

    // Uncomment the next line to enable auto-rotation
    // d3.timer(rotate);

  }, [originalLocation, antipodeLocation]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 overflow-hidden"
    >
      <motion.h3
        className="text-2xl font-bold text-white mb-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        🌍 Interactive Globe View
      </motion.h3>
      
      <div className="flex justify-center items-center">
        <div className="relative rounded-xl overflow-hidden shadow-2xl">
          <svg
            ref={svgRef}
            className="rounded-xl"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mt-6 text-center"
      >
        <p className="text-slate-300 text-sm mb-4">
          <span className="inline-flex items-center gap-2">
            <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
            Starting Point
          </span>
          <span className="mx-4">•</span>
          <span className="inline-flex items-center gap-2">
            <span className="w-3 h-3 bg-green-500 rounded-full"></span>
            Antipode
          </span>
          <span className="mx-4">•</span>
          <span className="inline-flex items-center gap-2">
            <span className="w-8 h-0.5 bg-purple-500"></span>
            Connection
          </span>
        </p>
        <p className="text-slate-400 text-xs">
          Drag to rotate the globe • Markers appear when locations are visible
        </p>
      </motion.div>

      <style jsx>{`
        .pulse-original {
          animation: pulse-blue 2s infinite;
        }
        .pulse-antipode {
          animation: pulse-green 2s infinite;
        }
        @keyframes pulse-blue {
          0% {
            r: 8;
            opacity: 0.6;
          }
          50% {
            r: 16;
            opacity: 0.2;
          }
          100% {
            r: 8;
            opacity: 0.6;
          }
        }
        @keyframes pulse-green {
          0% {
            r: 8;
            opacity: 0.6;
          }
          50% {
            r: 16;
            opacity: 0.2;
          }
          100% {
            r: 8;
            opacity: 0.6;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default MapDisplay;